/**
 * ============================================================================
 * NsatRegistrationForm — Service (API calls)
 * ============================================================================
 * Pure fetch wrappers. Lead posts to lead-create-new with
 * mx_Enquery_Source: "Enquire" and Custom5: "Not Verified" via formExtra.
 * ============================================================================
 */
import { getIpJSONData } from "@/context/ip.storage.utils";
import { leadCreateFetch } from "@/utils/connectCounsellor";
import {
  formatDate,
  generateUniqueId,
} from "@/components/molecules/Forms/ConnectModalFormIONSAT/utils";

export interface LeadCheckPayload {
  phoneNumber: string;
  emailId: string;
  fullName: string;
  countryCode: string;
}

/** Ensures/returns the anonymous uniqueLeadId used for lead-check dedupe. */
export function getOrCreateUniqueLeadId(): string {
  let uniqueId = localStorage.getItem("uniqueLeadId");
  if (!uniqueId) {
    uniqueId = generateUniqueId();
    localStorage.setItem("uniqueLeadId", uniqueId);
  }
  return uniqueId;
}

/**
 * POST /api/leadsquared.com/lead-check — silent, best-effort dedupe ping.
 * CountryCode clears when "Code".
 */
export async function checkExistingLead(
  payload: LeadCheckPayload,
): Promise<void> {
  try {
    const response = await fetch("/api/leadsquared.com/lead-check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        PhoneNo: payload.phoneNumber,
        EmailID: payload.emailId,
        Name: payload.fullName,
        CountryCode: payload.countryCode === "Code" ? "" : payload.countryCode,
        PageUrl: window.location.href,
        CommConsent: formatDate(new Date()),
        UniqueId: getOrCreateUniqueLeadId(),
      }),
    });

    if (!response.ok) throw new Error("lead-check failed");
  } catch (error) {
    console.error("Error in lead check:", error);
  }
}

export interface CreateNsatLeadOptions {
  form: Record<string, any>;
  meta: Record<string, any>;
  formExtra?: Record<string, any>;
}

/**
 * POST /api/salesforce.com/lead-create-new.
 * Always sends mx_Enquery_Source: "Enquire" at the body root.
 */
export async function createNsatLead({
  form,
  meta,
  formExtra,
}: CreateNsatLeadOptions) {
  const response = await leadCreateFetch("/api/salesforce.com/lead-create-new", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ipstack: getIpJSONData(),
      form: { ...form, ...formExtra },
      meta,
      mx_Enquery_Source: "Enquire",
    }),
  });

  if (typeof response === "string") {
    return JSON.parse(response as unknown as string);
  }
  return response.json();
}

/** POST /api/connect-counsellor. */
export { connectCounsellor } from "@/utils/connectCounsellor";

/**
 * POST /api/webengage — fire-and-forget with ContactId + TestURL.
 * Swallows errors (matches pre-MVC).
 */
export async function trackWebEngage({
  userId,
  TestURL,
}: {
  userId?: string;
  TestURL?: string;
}): Promise<void> {
  try {
    await fetch("/api/webengage", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        TestURL,
        // Slot: payload.slot,
      }),
    });
  } catch {
    // do nothing
  }
}
